import { createApp } from 'vue';
import { Capacitor } from '@capacitor/core';
import { IonicVue } from '@ionic/vue';
/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';
import '@ionic/vue/css/display.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/typography.css';
import { createPinia } from 'pinia';
// Cookie Consent plugin (vanilla-cookieconsent v3)
import CookieConsentVue from '@/plugins/CookieConsentVue';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

import externalLinks from '@/directives/externalLinks';
import { useAuthStore } from '@/stores/auth';
import App from './App.vue';
import router from './router';
/* Custom Global CSS */
import './theme/cookieconsent_custom.css';
import './theme/global.css';
import './theme/variables.css';

const pinia = createPinia();
const ukkApp = createApp(App);

ukkApp.directive('external-links', externalLinks);

ukkApp.use(IonicVue, {
  mode: 'md',
  animated: true,
  rippleEffect: false,
  hardwareBackButton: true,
  statusTap: true,
  swipeBackEnabled: true,
  navAnimation: undefined,
  keyboardClose: true,
});
ukkApp.use(CookieConsentVue);
ukkApp.use(pinia);

/**
 * Shared resume detection: native uses Capacitor appStateChange; browser/PWA uses visibilitychange.
 * Avoids treating cold-start “inactive → active” / first paint as a resume (needs initialCheckDone).
 */
const authResumeState = {
  initialCheckDone: false,
  hadBackgrounded: false,
};

function registerWebAuthResumeListener(
  authStore: ReturnType<typeof useAuthStore>,
): void {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (authResumeState.initialCheckDone) {
        authResumeState.hadBackgrounded = true;
      }
      return;
    }
    if (
      !authResumeState.initialCheckDone ||
      !authResumeState.hadBackgrounded
    ) {
      return;
    }
    authResumeState.hadBackgrounded = false;
    console.log('[auth] Browser tab visible again, running checkAccount...');
    void (async () => {
      try {
        const ok = await authStore.checkAccount();
        console.log(
          '[auth] checkAccount after tab resume finished, hasSession=',
          ok,
        );
      } catch (e) {
        console.error('[auth] checkAccount after tab resume failed:', e);
      }
    })();
  });
}

async function setupNativeApp(authStore: ReturnType<typeof useAuthStore>) {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const { App: CapacitorApp } = await import('@capacitor/app');

  CapacitorApp.addListener('appUrlOpen', async (event: { url: string }) => {
    console.log('App opened with URL:', event.url);
    const appOpenUrl = new URL(event.url);
    const matchesRegisteredRedirect = (registered: string | undefined) =>
      Boolean(
        registered &&
          (appOpenUrl.href.startsWith(registered) ||
            appOpenUrl.href.includes(registered)),
      );
    const isOAuthRedirect =
      matchesRegisteredRedirect(import.meta.env.VITE_MSAL_REDIRECT_URI) ||
      matchesRegisteredRedirect(
        import.meta.env.VITE_MSAL_REDIRECT_URI_NATIVE,
      );
    if (isOAuthRedirect) {
      console.log('MSAL redirect detected, handling auth...');
      await authStore.handleAuthRedirect(event.url);
    }
  });

  CapacitorApp.addListener(
    'appStateChange',
    async (state: { isActive: boolean }) => {
      if (!state.isActive) {
        if (authResumeState.initialCheckDone) {
          authResumeState.hadBackgrounded = true;
        }
        return;
      }
      if (
        !authResumeState.initialCheckDone ||
        !authResumeState.hadBackgrounded
      ) {
        return;
      }
      authResumeState.hadBackgrounded = false;
      console.log(
        '[auth] App resumed from background, running checkAccount...',
      );
      try {
        const ok = await authStore.checkAccount();
        console.log(
          '[auth] checkAccount after resume finished, hasSession=',
          ok,
        );
      } catch (e) {
        console.error('[auth] checkAccount after resume failed:', e);
      }
    },
  );
}

async function initializeApp() {
  try {
    const authStore = useAuthStore();
    await setupNativeApp(authStore);
    if (!Capacitor.isNativePlatform()) {
      registerWebAuthResumeListener(authStore);
    }

    // Check account BEFORE registering router to ensure
    // user is restored before router guards run.
    await authStore.checkAccount();
    authResumeState.initialCheckDone = true;
    console.log('Auth store checked account');

    // Now register router
    ukkApp.use(router);
    await router.isReady();
    console.log('Router is ready');

    ukkApp.mount('#app');
    console.log('App mounted');
  } catch (error) {
    console.error('Error initializing app:', error);
  }
}

initializeApp();
